import { BetslipUtilChannels, BetSlipViewsChannels, BsUtilReceivers } from "../dependencies";
import { InternalMsgRouter, ExternalMsgRouter } from "../routers";
import { LibConfig } from "../config";
import { SendMessageType } from "../types";
import { getMessages } from "../helpers";
/**
 * @class
 * @implements {IBsUtil}
 * @classdesc Mediates communication with Betslip
 */
export class BsUtil {
    constructor() {
        this.selectionsCount = 0;
        this.syncCounterWithFullBetslip = (shouldUpdate) => {
            if (shouldUpdate) {
                this.selectionsCount = this.betslip.getSelections().items.length;
            }
        };
        this.betslip = LibConfig.betslip;
        this.internalMsgRouter = new InternalMsgRouter(LibConfig.internalMsgBus);
        this.internalMsgRouter.attachListeners(this.onMessageReceivedInternal, this);
        this.internalMsgRouter.attachBetslipListeners(this.onMessageReceivedFromBetslip, this);
        if (LibConfig.externalBroadcastEnabled) {
            this.externalMsgRouter = new ExternalMsgRouter(LibConfig.externalMsgBus);
            this.externalMsgRouter.attachListeners(this.onMessageReceivedExternal, this);
        }
    }
    /**
     * @method getInstance
     * @description Get BSUtil instance
     */
    static getInstance() {
        if (!BsUtil.instance) {
            BsUtil.instance = new BsUtil();
        }
        return BsUtil.instance;
    }
    /**
     * @method add_items
     * @description Handler for BetslipUtilChannels.listen.commands.add_items message
     * @param {BsUtilAddItemsMessage} data Received data
     */
    add_items(data) {
        this.syncCounterWithFullBetslip(this.betslip.getSelections().items.length > this.selectionsCount);
        getMessages(SendMessageType.Add, this.selectionsCount, data.items)
            .forEach(message => {
            this.internalMsgRouter.sendMessage(BetSlipViewsChannels.Listen[message.type], {
                items: data.items,
                receiverIds: message.receiverIds
            });
        });
        this.sendMsgWithExternalMsgBus(BetSlipViewsChannels.Listen.addSelection, data);
        this.selectionsCount = this.selectionsCount + 1;
        this.selectionAdded(data, false);
    }
    /**
    * @method remove_items
    * @description Handler for BetslipUtilChannels.listen.commands.remove_items message
    * @param {BsUtilRemoveItemsMessage} data Received data
    */
    remove_items(data) {
        getMessages(SendMessageType.Remove, this.selectionsCount, data.items)
            .forEach(message => {
            this.internalMsgRouter.sendMessage(BetSlipViewsChannels.Listen[message.type], {
                items: message.items,
                receiverIds: message.receiverIds
            });
        });
        this.sendMsgWithExternalMsgBus(BetSlipViewsChannels.Listen.removeSelection, data);
        this.selectionsCount = this.selectionsCount - 1;
        this.selectionRemoved(data, false);
    }
    /**
    * @method get_status
    * @description Handler for BetslipUtilChannels.listen.commands.get_status message
    */
    get_status() {
        this.getBetslipStatus(this.betslip.getSelections());
    }
    /**
    * @method clear_slip
    * @description Handler for BetslipUtilChannels.listen.commands.clear_slip message
    */
    clear_slip() {
        const receiverIds = [];
        Object.keys(BsUtilReceivers).forEach(key => {
            receiverIds.push(BsUtilReceivers[key]);
        });
        this.sendMsgWithInternalMsgBus(BetSlipViewsChannels.Listen.clear, {
            receiverIds
        });
        this.sendMsgWithExternalMsgBus(BetSlipViewsChannels.Listen.clear);
        this.selectionsCount = 0;
    }
    sendMsgWithInternalMsgBus(channel, data) {
        this.internalMsgRouter.sendMessage(channel, data);
    }
    sendMsgWithExternalMsgBus(channel, data) {
        this.externalMsgRouter && this.externalMsgRouter.sendMessage(channel, data);
    }
    /**
    * @method selectionAdded
    * @description Handler for InternalMessageBusChannels.legacy.betslip.items.add message
    * @param {BsUtilAddItemsMessage} data Received data
    */
    selectionAdded(data, shouldUpdateCount = true) {
        this.sendMsgWithInternalMsgBus(BetslipUtilChannels.Broadcast.Items.added, data);
        this.sendMsgWithExternalMsgBus(BetslipUtilChannels.Broadcast.Items.added, data);
        this.syncCounterWithFullBetslip(shouldUpdateCount);
    }
    /**
    * @method selectionRemoved
    * @description Handler for InternalMessageBusChannels.legacy.betslip.items.remove message
    * @param {BsUtilRemoveItemsMessage} data Received data
    */
    selectionRemoved(data, shouldUpdateCount = true) {
        this.sendMsgWithInternalMsgBus(BetslipUtilChannels.Broadcast.Items.removed, data);
        this.sendMsgWithExternalMsgBus(BetslipUtilChannels.Broadcast.Items.removed, data);
        this.selectionsCount = this.betslip.getSelections().items.length;
    }
    /**
     * @method getBetslipStatus
     * @description Send BetslipUtilChannels.Broadcast.Status message
     * @param {BsUtilStatusMessage} data Betslip status message
     */
    getBetslipStatus(data) {
        this.sendMsgWithInternalMsgBus(BetslipUtilChannels.Broadcast.Status, data);
        this.sendMsgWithExternalMsgBus(BetslipUtilChannels.Broadcast.Status, data);
        this.sendMsgWithInternalMsgBus(BetSlipViewsChannels.Listen.status, data);
    }
    /**
    * @method onMessageReceivedInternal
    * @description Process messages from internal msg bus and execute corresponding handler method
    * @param {BsUtilInternalMsgBusMessage<BsUtilItemsMessage>} msg Received message
    */
    onMessageReceivedInternal(msg) {
        const handler = this.internalMsgRouter.listeners[`${msg.channel}.${msg.subChannel.join(".")}`];
        this[handler] && this[handler](...msg.data);
    }
    /**
     * @method onMessageReceivedFromBetslip
     * @description Process messages from internal msg bus for FullBetSlip and execute corresponding handler method
     * @param {BsUtilInternalMsgBusMessage<BsUtilItemsMessage>} msg Received message
     */
    onMessageReceivedFromBetslip(msg) {
        const handler = this.internalMsgRouter.betslipListeners[`${msg.channel}.${msg.subChannel.join(".")}`];
        this[handler] && this[handler](...msg.data);
    }
    /**
     * @method onMessageReceivedExternal
     * @description Process messages from external msg bus and execute corresponding handler method
     * @param {string} handler Handler method
     * @param {BsUtilItemsMessage} msg Received message
     */
    onMessageReceivedExternal(handler, msg) {
        this[handler] && this[handler](Object.assign({}, msg));
    }
}
//# sourceMappingURL=util.js.map